import React, { useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getSafeDisplayName } from '../utils/profanityFilter';

export default function MessageFeedback({ messageContent, user, darkMode }) {
  const [feedback, setFeedback] = useState(null); // 'up' or 'down'
  const [submitted, setSubmitted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);

  const db = getFirestore();

  const handleFeedback = async (type) => {
    if (submitted) return; // Prevent multiple submissions

    setFeedback(type);
    setSubmitted(true);

    try {
      // Send to admin dashboard
      await addDoc(collection(db, 'messageFeedback'), {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'anonymous',
        userName: getSafeDisplayName(user?.displayName) || 'Anonymous User',
        messageContent: messageContent,
        feedbackType: type, // 'up' or 'down'
        timestamp: serverTimestamp(),
        reviewed: false
      });

      console.log(`✅ Feedback (${type}) sent to admin dashboard`);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitted(false);
      setFeedback(null);
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(messageContent);
    setMenuOpen(false);
  };

  const handleReportMessage = async () => {
    if (!user || reportSubmitted) return;

    try {
      const db = getFirestore();
      await addDoc(collection(db, 'reportedMessages'), {
        messageContent,
        reportedBy: user.email || user.uid,
        reportedAt: serverTimestamp(),
        userId: user.uid
      });
      setReportSubmitted(true);
      setTimeout(() => {
        setMenuOpen(false);
        setReportSubmitted(false);
      }, 1500);
    } catch (err) {
      console.error('Error reporting message:', err);
    }
  };

  const buttonStyle = (type) => ({
    background: 'none',
    border: 'none',
    outline: 'none',
    borderRadius: '6px',
    cursor: submitted ? 'default' : 'pointer',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    transition: 'all 0.2s',
    opacity: submitted && feedback !== type ? 0.3 : 1
  });

  return (
    <div style={{
      display: 'inline-flex',
      gap: '6px',
      alignItems: 'center',
      marginLeft: '12px',
      position: 'relative'
    }}>
      <button
        onClick={() => handleFeedback('up')}
        disabled={submitted}
        style={buttonStyle('up')}
        title="Good response"
        onMouseEnter={(e) => {
          if (!submitted) {
            e.currentTarget.style.background = darkMode ? '#1f2937' : '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!submitted) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
        </svg>
      </button>
      
      <button
        onClick={() => handleFeedback('down')}
        disabled={submitted}
        style={buttonStyle('down')}
        title="Bad response"
        onMouseEnter={(e) => {
          if (!submitted) {
            e.currentTarget.style.background = darkMode ? '#1f2937' : '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!submitted) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
        </svg>
      </button>
      
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        style={buttonStyle('menu')}
        title="More options"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? '#1f2937' : '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={darkMode ? '#9ca3af' : '#6b7280'}
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="1"></circle>
          <circle cx="19" cy="12" r="1"></circle>
          <circle cx="5" cy="12" r="1"></circle>
        </svg>
      </button>

      {menuOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setMenuOpen(false)}
          />
          <div style={{
            position: 'absolute',
            top: '36px',
            right: '0',
            background: darkMode ? '#1f2937' : 'white',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '160px',
            zIndex: 1000,
            overflow: 'hidden'
          }}>
            <button
              onClick={handleCopyMessage}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                color: darkMode ? '#e5e7eb' : '#374151',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = darkMode ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              Copy message
            </button>
            <button
              onClick={handleReportMessage}
              disabled={reportSubmitted}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: reportSubmitted ? 'default' : 'pointer',
                color: reportSubmitted ? (darkMode ? '#6b7280' : '#9ca3af') : '#ef4444',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!reportSubmitted) {
                  e.currentTarget.style.background = darkMode ? '#374151' : '#fef2f2';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              {reportSubmitted ? 'Reported' : 'Report message'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

